import type { ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Clock, Ban } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiPost } from '../../lib/api';
import { Btn } from '../../components/primitives';

function Shell({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  const signOut = async () => {
    await apiPost('/api/auth/logout').catch(() => {});
    // Full reload: guarantees auth state and query cache are reset.
    window.location.replace('/login');
  };
  return (
    <div className="flex h-screen items-center justify-center bg-[#F6F7F9] dark:bg-slate-900 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
          {icon}
        </div>
        <h1 className="text-lg font-bold text-slate-800 dark:text-slate-100">{title}</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{body}</p>
        <Btn variant="secondary" className="mt-6" onClick={signOut}>
          Sign out
        </Btn>
      </div>
    </div>
  );
}

export function PendingScreen() {
  const { status } = useAuth();
  if (status === 'active') return <Navigate to="/" replace />;
  if (status === 'unauthenticated') return <Navigate to="/login" replace />;
  return (
    <Shell
      icon={<Clock />}
      title="Your account is awaiting access"
      body="You're signed in, but an administrator still needs to add you to the team. Please check back shortly."
    />
  );
}

export function NoAccessScreen() {
  const [params] = useSearchParams();
  const reason = params.get('reason');
  if (reason === 'not_invited') {
    return (
      <Shell
        icon={<Ban />}
        title="This workspace is invite-only"
        body="Your Google account isn't on the member list. Ask an administrator to invite your email address, then sign in again."
      />
    );
  }
  return (
    <Shell
      icon={<Ban />}
      title={reason === 'disabled' ? 'Your access has been disabled' : 'No access'}
      body={
        reason === 'disabled'
          ? 'Your account has been disabled. Contact an administrator if you think this is a mistake.'
          : 'You do not have access to this app.'
      }
    />
  );
}
